import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Trash2 } from "lucide-react";

const API  = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("dynamo_token")}` });

// Exact sheet tab names (trailing spaces preserved for API calls)
const SHEETS = ["Tuesday", "Wednesday ", "Thursday", "Friday ", "Saturday"];

const COLUMNS = [
  { key: "child_name",    label: "Full Name"  },
  { key: "session_type",  label: "Session"    },
  { key: "class_offered", label: "Class"      },
  { key: "start_date",    label: "Start Date" },
  { key: "confirmed",     label: "Confirmed"  },
  { key: "attended",      label: "Attended"   },
  { key: "notes",         label: "Notes"      },
];

function SkeletonRow() {
  return (
    <tr>
      <td className="px-4 py-3 w-10">
        <div className="h-4 w-4 bg-slate-100 rounded animate-pulse" />
      </td>
      {COLUMNS.map(c => (
        <td key={c.key} className="px-4 py-3">
          <div className="h-4 bg-slate-100 rounded animate-pulse" />
        </td>
      ))}
    </tr>
  );
}

export default function InvitesTab() {
  const [sheet, setSheet]               = useState(SHEETS[0]);
  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [deleting, setDeleting]         = useState(false);

  useEffect(() => { fetchData(SHEETS[0]); }, []);

  async function fetchData(s) {
    setLoading(true);
    setSelectedRows(new Set());
    try {
      const { data } = await axios.get(`${API}/invites?sheet=${encodeURIComponent(s)}`, { headers: auth() });
      setRows(data.data || []);
    } catch {
      toast.error("Failed to load invites");
    } finally {
      setLoading(false);
    }
  }

  function switchSheet(s) {
    setSheet(s);
    setRows([]);
    fetchData(s);
  }

  async function handleDelete() {
    const count = selectedRows.size;
    if (!window.confirm(
      `Delete ${count} row${count !== 1 ? "s" : ""} from the ${sheet.trim()} sheet?\n\nThis will permanently remove them from Google Sheets.`
    )) return;

    setDeleting(true);
    try {
      await axios.post(`${API}/invites/delete-rows`, {
        sheet,
        row_indices: [...selectedRows],
      }, { headers: auth() });
      toast.success(`${count} row${count !== 1 ? "s" : ""} deleted`);
      fetchData(sheet);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Invites
          </h2>
          {!loading && (
            <span className="text-xs text-slate-400">
              {rows.length} entr{rows.length === 1 ? "y" : "ies"}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Day sub-tabs */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {SHEETS.map(s => (
              <button
                key={s}
                data-testid={`invite-sheet-${s.trim().toLowerCase()}`}
                onClick={() => switchSheet(s)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap
                  ${sheet === s
                    ? "bg-white text-[#1656a4] shadow-sm"
                    : "text-slate-500 hover:text-slate-700"}`}
              >
                {s.trim()}
              </button>
            ))}
          </div>

          {/* Delete button — appears when rows selected */}
          {selectedRows.size > 0 && (
            <button
              data-testid="delete-invites-button"
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-1.5 bg-[#ff3131] hover:bg-[#dd1010]
                         text-white text-sm font-medium px-3.5 py-2 rounded-md
                         transition-colors disabled:opacity-60"
            >
              <Trash2 size={14} />
              {deleting ? "Deleting…" : `Delete (${selectedRows.size})`}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto" data-testid="invites-table-wrapper">
        <table className="w-full text-sm" data-testid="invites-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {/* Select-all checkbox */}
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  data-testid="invites-select-all"
                  checked={rows.length > 0 && selectedRows.size === rows.length}
                  ref={el => {
                    if (el) el.indeterminate = selectedRows.size > 0 && selectedRows.size < rows.length;
                  }}
                  onChange={e =>
                    setSelectedRows(e.target.checked ? new Set(rows.map((_, i) => i)) : new Set())
                  }
                  className="w-4 h-4 rounded border-slate-300 cursor-pointer accent-[#1656a4]"
                />
              </th>
              {COLUMNS.map(c => (
                <th
                  key={c.key}
                  className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length + 1}
                  className="px-4 py-12 text-center text-slate-400"
                >
                  No invite records for {sheet.trim()}.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={idx}
                  data-testid={`invite-row-${idx}`}
                  className="transition-colors cursor-pointer"
                  style={row.highlight_color ? { backgroundColor: row.highlight_color } : undefined}
                  onMouseEnter={e => {
                    if (!row.highlight_color) e.currentTarget.style.backgroundColor = "#f8fafc";
                  }}
                  onMouseLeave={e => {
                    if (!row.highlight_color) e.currentTarget.style.backgroundColor = "";
                    else e.currentTarget.style.backgroundColor = row.highlight_color;
                  }}
                  onClick={() =>
                    setSelectedRows(prev => {
                      const next = new Set(prev);
                      next.has(idx) ? next.delete(idx) : next.add(idx);
                      return next;
                    })
                  }
                >
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      data-testid={`invite-checkbox-${idx}`}
                      checked={selectedRows.has(idx)}
                      onChange={() =>
                        setSelectedRows(prev => {
                          const next = new Set(prev);
                          next.has(idx) ? next.delete(idx) : next.add(idx);
                          return next;
                        })
                      }
                      className="w-4 h-4 rounded border-slate-300 cursor-pointer accent-[#1656a4]"
                    />
                  </td>
                  {COLUMNS.map(c => (
                    <td key={c.key} className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {row[c.key] || <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
